import { format } from 'date-fns';

import { z } from 'zod';
import { tryCatch } from '@/app/utils/trycatch';
import { parseApiResponse, parsePaginatedResponse } from '@/app/utils/api-response';
import type { ApiResult } from '@/app/utils/api-errors';

/** Parse a fetch Response into an ApiResult (reads the JSON body). */
async function parseApiResult(response: Response): Promise<ApiResult> {
  try {
    const json = await response.json();
    if (typeof json === 'object' && json !== null && 'success' in json) {
      return json as ApiResult;
    }
    // Unexpected shape — treat as success if HTTP was ok
    return { success: response.ok };
  } catch {
    return { success: response.ok };
  }
}

export interface FetchTodosOptions {
  status?: string;
  next_page?: string | null;
  limit?: number;
  unassigned?: boolean;
}

export interface FetchReportsOptions {
  next_page?: string | null;
  limit?: number;
  status?: string;
}

export interface FetchReportOptionsParams {
  status?: string;
  search?: string;
  limit?: number;
  next_page?: string | null;
}

export interface ReportOption {
  id: number;
  uuid: string;
  title: string;
  status?: string;
}
import {
  AnalyticsExportFilters,
  BankDetails,
  CategoryWithRules,
  CompanySetupStatus,
  CompanySetupSteps,
  Expense,
  ExpenseWithHistory,
  Mileage,
  MileageWithHistory,
  ExpenseInsert,
  ExpenseStatus,
  ExportType,
  InviteRoles,
  MilageInsert,
  MileageUpdate,
  ReportCollections,
  ReportCollectionScheme,
  ReportCollectionsScheme,
  ReportStatus,
  OrgRoleEnum,
  OrgRoleNameEnum,
  Roles,
  Rule,
  Rules,
  Client,
  Settlement,
  SettlementTransaction,
  Split,
  TodosSummarySchema,
  TodosSummary,
  ReportsSummarySchema,
  ReportsSummary,
  selectCategorySchema,
  selectClientsSchema,
  selectCostCentresSchema,
  selectExpensesSchema,
  selectExpenseWithHistorySchema,
  selectMileageSchema,
  selectMileageWithHistorySchema,
  selectOrganizationSchema,
  selectRuleSchema,
  selectRulesSchema,
  selectUserSchema,
  selectUsersSchema,
  UserMembershipSchema,
} from '@/types/types';
import { errorTracker } from './errorTracking';

// Scoped fetch wrapper to automatically handle Request and Flow IDs
const originalFetch = globalThis.fetch;
const fetch = async (
  input: RequestInfo | URL,
  init?: RequestInit,
  flowId?: string | null,
): Promise<Response> => {
  const headers = new Headers(init?.headers);

  if (flowId) {
    headers.set('X-Flow-Id', flowId);
  }

  try {
    const response = await originalFetch(input, {
      ...init,
      headers,
    });

    const requestId = response.headers.get('x-request-id') || response.headers.get('X-Request-Id');

    if (process.env.NODE_ENV === 'development') {
      const url = input.toString();
      if (response.ok) {
        console.log(
          `[API Success] ${url} | Request ID: ${requestId ?? 'none'} | Flow ID: ${flowId ?? 'none'}`,
        );
      } else {
        console.error(
          `[API Failure] ${url} | Request ID: ${requestId ?? 'none'} | Flow ID: ${flowId ?? 'none'} | Status: ${response.status}`,
        );
      }
    }

    return response;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(`[API Error] fetch: ${input.toString()} | Flow ID: ${flowId || 'none'}`, error);
    }
    throw error;
  }
};

function fetchTodos(
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  role: Roles,
  options: FetchTodosOptions & ({ limit: number } | { next_page: string } | { status: string }),
): Promise<{ data: (Expense | Mileage)[]; next_page: string | null }>;
function fetchTodos(
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  role: Roles,
  options?: FetchTodosOptions,
): Promise<(Expense | Mileage)[]>;
async function fetchTodos(
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  role: Roles,
  options?: FetchTodosOptions,
): Promise<{ data: (Expense | Mileage)[]; next_page: string | null } | (Expense | Mileage)[]> {
  const token = await getToken();
  if (!token) {
    console.error('Authentication token not available');
    return options &&
      (options.next_page !== undefined ||
        options.limit !== undefined ||
        options.status !== undefined)
      ? { data: [], next_page: null }
      : [];
  }
  const params = new URLSearchParams({
    org: organizationUUID,
    m: role === 'manager' || role === 'accountant' ? 'true' : 'false',
  });

  if (options?.status) {
    params.append('status', options.status);
  }
  if (options?.next_page) {
    params.append('next_page', options.next_page);
  }
  if (options?.limit !== undefined) {
    params.append('limit', String(options.limit));
  }
  if (options?.unassigned === true) {
    params.append('unassigned', 'true');
  }

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/todos?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (response.status === 200) {
    const responseJSON = await response.json();
    const todoSchema = z.array(selectExpensesSchema.or(selectMileageSchema));

    if (
      options &&
      (options.next_page !== undefined ||
        options.limit !== undefined ||
        options.status !== undefined)
    ) {
      const paginated = parsePaginatedResponse(responseJSON);
      const [error, todos] = tryCatch(() => todoSchema.parse(paginated.data));
      if (error) {
        errorTracker.captureException(error, { tags: { message: 'Error parsing todos' } });
        return { data: [], next_page: null };
      }
      return { data: todos, next_page: paginated.next_page };
    }

    const rawData = parseApiResponse(responseJSON);
    const [error, todos] = tryCatch(() => todoSchema.parse(rawData));
    if (error) {
      errorTracker.captureException(error, { tags: { message: 'Error parsing todos' } });
      return [];
    }
    return todos;
  }
  return options &&
    (options.next_page !== undefined || options.limit !== undefined || options.status !== undefined)
    ? { data: [], next_page: null }
    : [];
}

function fetchFilteredExpenses(
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  role: Roles,
  from?: Date,
  to?: Date,
  selectedEmployees?: string[],
  isOwedToEmployee?: boolean,
  isPrivateExpense?: boolean,
  amountFrom?: number,
  amountTo?: number,
  selectedClients?: string[],
  selectedCostcentres?: string[],
): Promise<(Expense | Mileage)[]>;
function fetchFilteredExpenses(
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  role: Roles,
  from?: Date,
  to?: Date,
  selectedEmployees?: string[],
  isOwedToEmployee?: boolean,
  isPrivateExpense?: boolean,
  amountFrom?: number,
  amountTo?: number,
  selectedClients?: string[],
  selectedCostcentres?: string[],
  next_page?: string | null,
  limit?: number,
): Promise<{ data: (Expense | Mileage)[]; next_page: string | null } | (Expense | Mileage)[]>;
async function fetchFilteredExpenses(
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  role: Roles,
  from?: Date,
  to?: Date,
  selectedEmployees?: string[],
  isOwedToEmployee?: boolean,
  isPrivateExpense?: boolean,
  amountFrom?: number,
  amountTo?: number,
  selectedClients?: string[],
  selectedCostcentres?: string[],
  next_page?: string | null,
  limit?: number,
): Promise<{ data: (Expense | Mileage)[]; next_page: string | null } | (Expense | Mileage)[]> {
  const token = await getToken();
  if (!token) {
    console.error('Authentication token not available');
    return next_page !== undefined || limit !== undefined ? { data: [], next_page: null } : [];
  }
  const _amountFrom = amountFrom === 0 ? undefined : amountFrom;
  const _amountTo = amountTo === 0 || amountTo === Number.POSITIVE_INFINITY ? undefined : amountTo;
  // Build params object
  const params: Record<string, string> = {
    org: organizationUUID,
    m: role === 'manager' || role === 'accountant' ? 'true' : 'false',
  };
  if (from) params.from = from.toISOString();
  if (to) params.to = to.toISOString();
  if (selectedEmployees && selectedEmployees.length > 0)
    params.userIds = selectedEmployees.join(',');
  if (isOwedToEmployee !== undefined) params.owed = String(isOwedToEmployee);
  if (isPrivateExpense !== undefined) params.private = String(isPrivateExpense);
  if (_amountFrom !== undefined) params.agte = String(_amountFrom);
  if (_amountTo !== undefined) params.alte = String(_amountTo);
  if (selectedClients && selectedClients.length > 0) params.clients = selectedClients.join(',');
  if (selectedCostcentres && selectedCostcentres.length > 0)
    params.costcentres = selectedCostcentres.join(',');
  if (next_page) params.next_page = next_page;
  if (limit !== undefined) params.limit = String(limit);

  const queryString = new URLSearchParams(params).toString();

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/todos?${queryString}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (response.status === 200) {
    const responseJSON = await response.json();
    const todoSchema = z.array(selectExpensesSchema.or(selectMileageSchema));

    if (next_page !== undefined || limit !== undefined) {
      const paginated = parsePaginatedResponse(responseJSON);
      const [error, todos] = tryCatch(() => todoSchema.parse(paginated.data));
      if (error) {
        errorTracker.captureException(error, {
          tags: { message: 'Error fetch filtered expenses' },
        });
        return { data: [], next_page: null };
      }
      return { data: todos, next_page: paginated.next_page };
    }

    const rawData = parseApiResponse(responseJSON);
    const [error, todos] = tryCatch(() => todoSchema.parse(rawData));
    if (error) {
      errorTracker.captureException(error, { tags: { message: 'Error fetch filtered expenses' } });
      return [];
    }
    return todos;
  }
  return next_page !== undefined || limit !== undefined ? { data: [], next_page: null } : [];
}

function fetchReports(
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  role: Roles,
  reportIds: number[] | undefined,
  options: FetchReportsOptions & ({ limit: number } | { next_page: string }),
): Promise<{ data: ReportCollections; next_page: string | null }>;
function fetchReports(
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  role: Roles,
  reportIds?: number[],
  options?: FetchReportsOptions,
): Promise<ReportCollections>;
async function fetchReports(
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  role: Roles,
  reportIds?: number[],
  options?: FetchReportsOptions,
): Promise<{ data: ReportCollections; next_page: string | null } | ReportCollections> {
  const token = await getToken();
  const params = new URLSearchParams({
    org: organizationUUID,
    role,
  });
  if (reportIds && reportIds.length > 0) {
    params.append('reportIds', reportIds.join(','));
  }
  if (options?.next_page) {
    params.append('next_page', options.next_page);
  }
  if (options?.limit !== undefined) {
    params.append('limit', String(options.limit));
  }
  if (options?.status) {
    params.append('status', options.status);
  }

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/reports?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (response.status === 200) {
    const responseJSON = await response.json();
    try {
      if (options && (options.next_page !== undefined || options.limit !== undefined)) {
        const paginated = parsePaginatedResponse(responseJSON);
        const reports = ReportCollectionsScheme.parse(paginated.data);
        return { data: reports, next_page: paginated.next_page };
      }

      const rawData = parseApiResponse(responseJSON);
      const reports = ReportCollectionsScheme.parse(rawData);
      return reports;
    } catch (error) {
      console.error('fetch reports error:', error);
      return options && (options.next_page !== undefined || options.limit !== undefined)
        ? { data: [], next_page: null }
        : [];
    }
  }
  return options && (options.next_page !== undefined || options.limit !== undefined)
    ? { data: [], next_page: null }
    : [];
}

const fetchReportOptions = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  role: Roles,
  params?: FetchReportOptionsParams,
): Promise<{ data: ReportOption[]; next_page: string | null }> => {
  const token = await getToken();
  if (!token) return { data: [], next_page: null };

  const query = new URLSearchParams({
    org: organizationUUID,
    role,
  });
  if (params?.status) query.append('status', params.status);
  if (params?.search) query.append('search', params.search);
  if (params?.limit !== undefined) query.append('limit', String(params.limit));
  if (params?.next_page) query.append('next_page', params.next_page);

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/reports/options?${query.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (response.status === 200) {
    const responseJSON = await response.json();
    const reportOptionSchema = z.array(
      z.object({
        id: z.number(),
        uuid: z.string(),
        title: z.string(),
        status: z.string().optional(),
      }),
    );

    // Support paginated response
    const paginated = parsePaginatedResponse(responseJSON);
    if (paginated.data) {
      const parsed = reportOptionSchema.safeParse(paginated.data);
      if (parsed.success) {
        return { data: parsed.data as ReportOption[], next_page: paginated.next_page };
      }
      console.error('fetchReportOptions parse error:', parsed.error);
    } else {
      const rawData = parseApiResponse(responseJSON);
      const parsed = reportOptionSchema.safeParse(rawData);
      if (parsed.success) {
        return { data: parsed.data as ReportOption[], next_page: null };
      }
      console.error('fetchReportOptions parse error:', parsed.error);
    }
  }
  return { data: [], next_page: null };
};

const fetchEmployees = async (getToken: () => Promise<string | null>, organizationUUID: string) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/employees?org=${organizationUUID}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (response.status === 200) {
    const responseJSON = await response.json();
    try {
      const employees = selectUsersSchema.parse(parseApiResponse(responseJSON));

      return employees;
    } catch (error) {
      console.error('fetch employees error:', error);
    }
  }
  return [];
};

const trackConversion = async (token: string, orgId: string) => {
  // mark the conversion as tracked in the backend so we don't track it again
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/conversion?org=${orgId}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return parseApiResult(response);
};

const fetchOrganization = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization?org=${organizationUUID}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (response.status === 200) {
    try {
      const organizationJSON = await response.json();
      const organization = selectOrganizationSchema.parse(parseApiResponse(organizationJSON));
      return organization;
    } catch (error) {
      console.error('Error parsing organization', error);
      return null;
    }
  } else {
    return null;
  }
};

const createOrganization = async (
  token: string,
  name: string,
  slug: string,
  extras?: {
    default_currency?: string;
    default_country?: string;
    defer_company_setup?: boolean;
  },
): Promise<{ data: string | null; code: string }> => {
  const body: {
    name: string;
    slug: string;
    default_currency?: string;
    default_country?: string;
    defer_company_setup?: boolean;
  } = { name, slug };
  if (extras?.default_currency) {
    body.default_currency = extras.default_currency;
  }
  if (extras?.default_country) {
    body.default_country = extras.default_country;
  }
  if (extras?.defer_company_setup) {
    body.defer_company_setup = true;
  }

  const response = await fetch(`${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (response.status === 201) {
    const responseJSON = await response.json();
    return { data: parseApiResponse<string>(responseJSON) ?? null, code: 'success' };
  }
  if (response.status === 409) {
    return { data: null, code: 'form_identifier_exists' };
  }
  return { data: null, code: 'error' };
};

const getUpcomingReceipt = async (token: string, organizationUUID: string, quantity: number) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/receipts/upcoming?org=${organizationUUID}&quantity=${quantity}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (response.status === 200) {
    const responseJSON = await response.json();
    const schema = z
      .object({
        amountDue: z.number(),
        nextInvoiceDate: z.string(),
        nextInvoiceAmount: z.number(),
      })
      .or(z.number());
    const [error, data] = tryCatch(() => schema.parse(parseApiResponse(responseJSON)));
    if (error) {
      console.error('Error parsing upcoming receipt', error);
      return null;
    }
    if (typeof data === 'number') {
      return null;
    }
    return data;
  } else {
    const err = await response.json();
    console.error('getUpcomingReceipt failed', err);
    return null;
  }
};

const updateSeats = async (
  token: string,
  organizationUUID: string,
  seats: number,
  shouldProrateNow: boolean,
) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/seats?org=${organizationUUID}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ seats, shouldProrateNow }),
    },
  );
  if (response.status === 200) {
    return true;
  } else {
    const err = await response.json();
    console.error('updateSeats failed', err);
    return false;
  }
};

const fetchSeatStatus = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
) => {
  const token = await getToken();
  if (!token) {
    return null;
  }
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/seat-status?org=${organizationUUID}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (response.ok) {
    const responseJSON = await response.json();
    const schema = z.enum(['active', 'inactive', 'pending_deactivation']);
    const [error, data] = tryCatch(() => schema.parse(parseApiResponse(responseJSON)));
    if (error) {
      return null;
    }
    return data;
  }
  return null;
};

const organizationImageDataSchema = z.object({
  image_url: z.string().nullable(),
  has_image: z.boolean(),
});

const organizationImageResponseSchema = z.object({
  success: z.literal(true),
  data: organizationImageDataSchema,
});

const parseOrganizationImageResponse = (responseJSON: unknown) => {
  const [error, data] = tryCatch(() => organizationImageResponseSchema.parse(responseJSON));
  if (error) {
    console.error('Error parsing organization image', error);
    return null;
  }
  return data.data;
};

const fetchOrganizationImage = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
) => {
  const token = await getToken();
  if (!token) {
    return null;
  }
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/image?org=${organizationUUID}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    console.error('fetchOrganizationImage failed', response.status, err);
    return null;
  }
  return parseOrganizationImageResponse(await response.json());
};

const uploadOrganizationImage = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  file: File,
) => {
  const token = await getToken();
  if (!token) {
    return { success: false } as any;
  }
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/image?org=${organizationUUID}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    },
  );
  return parseApiResult(response);
};

const deleteOrganizationImage = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
) => {
  const token = await getToken();
  if (!token) {
    return { success: false } as any;
  }
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/image?org=${organizationUUID}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return parseApiResult(response);
};

const fetchOrganizationMemberships = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/memberships?org=${organizationUUID}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (response.status === 200) {
    const responseJSON = await response.json();

    const membershipsSchema = z.object({
      data: z.array(
        z.object({
          id: z.number(),
          role: OrgRoleEnum,
          role_name: OrgRoleNameEnum,
          created_at: z.string(),
          status: z.enum(['pending', 'active', 'deleted', 'inactive']),
          seat_status: z.enum(['active', 'inactive', 'pending_deactivation']),
          organization: z.object({
            name: z.string(),
          }),
          last_active: z.string().nullable(),
          user: z.object({
            first_name: z.string().nullable(),
            last_name: z.string().nullable(),
            user_id: z.string(),
            email: z.string(),
          }),
        }),
      ),
    });
    const invitesSchema = z.object({
      data: z.array(
        z.object({
          id: z.string(),
          role: z.string(),
          email_address: z.string(),
          created_at: z.number(),
          status: z.string(),
        }),
      ),
    });
    const combinedSchema = z.object({
      memberships: membershipsSchema,
      invites: invitesSchema,
    });
    const [error, data] = tryCatch(() => combinedSchema.parse(parseApiResponse(responseJSON)));
    if (error) {
      errorTracker.captureException(error, {
        tags: { message: 'Error parsing organization memberships' },
      });
      return null;
    }
    return data;
  }
  return null;
};

const fetchUserMemberships = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/user`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (response.status === 200) {
    const responseJSON = await response.json();
    const data = UserMembershipSchema.array().parse(parseApiResponse(responseJSON));
    return data;
  }
  return null;
};

const updateMembershipRole = async (
  token: string,
  organizationUUID: string,
  userId: string,
  role: string,
) => {
  const backendRole = getRoleForBackend(role);
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/memberships?org=${organizationUUID}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ role: backendRole, userId }),
    },
  );
  return parseApiResult(response);
};

const inviteUsers = async (
  token: string,
  organizationUUID: string,
  emails: string[],
  role: InviteRoles,
) => {
  const backendRole = getRoleForBackend(role);

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/invitations?org=${organizationUUID}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ emails, role: backendRole }),
    },
  );
  if (response.status === 201) {
    return true;
  }
  const err = await response.json();
  console.error('fail', err);
  return false;
};

const removeMember = async (token: string, organizationUUID: string, userId: string) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/memberships?org=${organizationUUID}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId }),
    },
  );
  if (response.status === 200) {
    return true;
  }
  const err = await response.json();
  console.error('removeMember failed', err);
  return false;
};

const revokeInvite = async (token: string, organizationUUID: string, invitationId: string) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/invitations?org=${organizationUUID}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ invitationId }),
    },
  );
  return parseApiResult(response);
};

export interface ShareReportResultData {
  downloadUrl?: string;
  delivery?: string;
  [key: string]: unknown;
}

const shareReport = async (
  token: string,
  organizationUUID: string,
  reportUUID: string,
  recipients: string[],
  type: ExportType,
  lng: string,
  role: Roles,
  includeReceipts = false,
) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/reports/share?org=${organizationUUID}&m=${
      role === 'manager' || role === 'accountant' ? 'true' : 'false'
    }`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Accept-Language': lng,
      },
      body: JSON.stringify({ recipients, type, report: reportUUID, includeReceipts }),
    },
  );

  if (response.status === 200 || response.status === 201) {
    const responseJSON = await response.json().catch(() => null);
    return {
      success: true,
      data: parseApiResponse<ShareReportResultData>(responseJSON),
    };
  }
  console.error('shareReport failed', response.status);
  return { success: false } as any;
};

const shareExpenses = async (
  token: string,
  organizationUUID: string,
  recipients: string[],
  type: ExportType,
  lng: string,
  role: Roles,
  includeReceipts = false,
  filters?: AnalyticsExportFilters,
) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/expenses/share?org=${organizationUUID}&m=${
      role === 'manager' || role === 'accountant' ? 'true' : 'false'
    }`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Accept-Language': lng,
      },
      body: JSON.stringify({ recipients, type, filters, includeReceipts }),
    },
  );

  if (response.status === 200 || response.status === 201) {
    const responseJSON = await response.json().catch(() => null);
    return {
      success: true,
      data: parseApiResponse<ShareReportResultData>(responseJSON),
    };
  }
  console.error('shareExpenses failed', response.status);
  return { success: false } as any;
};

export type FetchReportResult = FetchResult<z.infer<typeof ReportCollectionScheme>>;

const fetchReport = async (
  getToken: () => Promise<string | null>,
  id: string,
  organizationUUID: string | undefined,
  lng: string,
): Promise<FetchReportResult> => {
  if (!organizationUUID) return { status: 'unknown_error' };
  return fetchTypedResource({
    getToken,
    url: `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/reports/${id}?org=${organizationUUID}`,
    schema: ReportCollectionScheme,
    component: 'fetchReport',
    resourceId: id,
    extraHeaders: { 'Accept-Language': lng },
  });
};

const fetchExpenses = async (
  getToken: () => Promise<string | null>,
  from: Date | null,
  to: Date | null,
  organizationUUID: string | undefined,
  manager = false,
) => {
  if (!organizationUUID || !from || !to) return [];
  const token = await getToken();
  const response = await fetch(
    `${
      process.env.NEXT_PUBLIC_DOCOMONDO_URL
    }/api/v1/expenses?from=${from}&to=${to}&org=${organizationUUID}&m=${
      manager ? 'true' : 'false'
    }`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (response.status === 200) {
    const responseJSON = await response.json();
    try {
      const expenses = selectExpensesSchema.array().parse(parseApiResponse(responseJSON));

      return expenses;
    } catch (error) {
      console.error('fetch expenses error:', error);
    }
  }
  return [];
};

export type FetchResult<T> =
  | { status: 'success'; data: T }
  | { status: 'not_found' }
  | { status: 'forbidden' }
  | { status: 'parse_error'; error: unknown }
  | { status: 'network_error'; error: unknown }
  | { status: 'unknown_error' };

interface FetchTypedResourceOptions<T> {
  getToken: () => Promise<string | null>;
  url: string;
  schema: z.ZodType<T, z.ZodTypeDef, unknown>;
  component: string;
  resourceId: string;
  extraHeaders?: Record<string, string>;
}

async function fetchTypedResource<T>({
  getToken,
  url,
  schema,
  component,
  resourceId,
  extraHeaders,
}: FetchTypedResourceOptions<T>): Promise<FetchResult<T>> {
  try {
    let parsedUrl: URL;
    try {
      parsedUrl = new URL(url);
    } catch {
      console.error(`Invalid URL fetching ${component}`, url);
      return { status: 'unknown_error' };
    }

    const token = await getToken();
    const response = await fetch(parsedUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...extraHeaders,
      },
    });

    if (response.status === 200) {
      try {
        const responseJSON = await response.json();
        const data = schema.parse(parseApiResponse(responseJSON));
        return { status: 'success', data };
      } catch (error) {
        console.error(`Error parsing ${component}`, error);
        errorTracker.captureException(error instanceof Error ? error : new Error(String(error)), {
          extra: { resourceId },
          tags: { component, type: 'parse_error' },
        });
        return { status: 'parse_error', error };
      }
    } else if (response.status === 404) {
      return { status: 'not_found' };
    } else if (response.status === 403 || response.status === 401) {
      return { status: 'forbidden' };
    }

    console.error(`Unexpected status ${response.status} fetching ${component}`);
    errorTracker.captureException(
      new Error(`Unexpected status ${response.status} fetching ${component}`),
      {
        extra: { resourceId, status: response.status },
        tags: { component, type: 'unknown_error' },
      },
    );
    return { status: 'unknown_error' };
  } catch (error) {
    console.error(`Network or unexpected error fetching ${component}`, error);
    return { status: 'network_error', error };
  }
}

export type FetchExpenseResult = FetchResult<ExpenseWithHistory>;

const fetchExpense = async (
  getToken: () => Promise<string | null>,
  id: string,
): Promise<FetchExpenseResult> =>
  fetchTypedResource({
    getToken,
    url: `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/expenses/${id}`,
    schema: selectExpenseWithHistorySchema,
    component: 'fetchExpense',
    resourceId: id,
  });

const addExpense = async (
  getToken: () => Promise<string | null>,
  expense: ExpenseInsert,
  organizationUUID: string,
  splits?: Split[],
) => {
  expense.amount = parseFloat(expense.amount).toFixed(2);
  expense.vat = expense.vat ? parseFloat(expense.vat).toFixed(2) : null;

  type FormattedSplit = Omit<Split, 'vat'> & { vat?: string | null };

  // Format splits if provided
  const formattedSplits = splits?.map((split) => {
    const formatted: FormattedSplit = {
      ...split,
      amount: isNaN(parseFloat(split.amount)) ? '0.00' : parseFloat(split.amount).toFixed(2),
      amount_default_currency: isNaN(parseFloat(split.amount_default_currency))
        ? '0.00'
        : parseFloat(split.amount_default_currency).toFixed(2),
    };
    if (split.vat !== undefined && split.vat !== null) {
      formatted.vat = !isNaN(parseFloat(split.vat)) ? parseFloat(split.vat).toFixed(2) : null;
    } else {
      delete formatted.vat;
    }
    return formatted;
  });

  const token = await getToken();
  const response = await fetch(`${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/expenses/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      expense,
      organization: organizationUUID,
      splits: formattedSplits || [],
    }),
  });

  if (response.status === 201) {
    const responseJSON = await response.json();
    const idSchema = z.object({
      insertId: z.number().optional(),
      id: z.number().optional(),
    });
    const parsed = idSchema.safeParse(parseApiResponse(responseJSON) ?? responseJSON);
    if (parsed.success && (parsed.data.id !== undefined || parsed.data.insertId !== undefined)) {
      return (parsed.data.id ?? parsed.data.insertId) as number;
    }
  }
  return false;
};

const updateExpense = async (
  getToken: () => Promise<string | null>,
  expense: Expense,
  comment?: string,
  splits?: Split[],
) => {
  expense.amount = parseFloat(expense.amount).toFixed(2);
  expense.amount_default_currency = parseFloat(expense.amount_default_currency).toFixed(2);
  expense.vat = expense.vat ? parseFloat(expense.vat).toFixed(2) : null;

  type FormattedSplit = Omit<Split, 'vat'> & { vat?: string | null };

  // Format splits if provided
  const formattedSplits = splits?.map((split) => {
    const formatted: FormattedSplit = {
      ...split,
      amount: isNaN(parseFloat(split.amount)) ? '0.00' : parseFloat(split.amount).toFixed(2),
      amount_default_currency: isNaN(parseFloat(split.amount_default_currency))
        ? '0.00'
        : parseFloat(split.amount_default_currency).toFixed(2),
    };
    if (split.vat !== undefined && split.vat !== null) {
      formatted.vat = !isNaN(parseFloat(split.vat)) ? parseFloat(split.vat).toFixed(2) : null;
    } else {
      delete formatted.vat;
    }
    return formatted;
  });

  const token = await getToken();
  const response = await fetch(`${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/expenses/`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: comment
      ? JSON.stringify({ expense, comment, splits: formattedSplits || [] })
      : JSON.stringify({ expense, splits: formattedSplits || [] }),
  });

  return parseApiResult(response);
};

const fetchCostCentres = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/costcentres?org=${organizationUUID}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (response.status === 200) {
    const responseJSON = await response.json();
    try {
      const costcentres = selectCostCentresSchema.parse(parseApiResponse(responseJSON));

      return costcentres;
    } catch (error) {
      console.error('fetch costcentres error:', error);
    }
  }
  return [];
};

const fetchUser = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  const response = await fetch(`${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/user`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 200) {
    const responseJSON = await response.json();
    try {
      const user = selectUserSchema
        .extend({
          token: z.string(), // intercom jwt token
        })
        .omit({ api_token: true })
        .parse(parseApiResponse(responseJSON));
      return user;
    } catch (error) {
      console.error('fetch user error:', error);
    }
  }
  return null;
};

const fetchIssuers = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  search?: string,
  limit?: number,
) => {
  const token = await getToken();
  const query = new URLSearchParams({ org: organizationUUID });
  if (search) query.append('search', search);
  if (limit !== undefined) query.append('limit', String(limit));

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/issuers?${query.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (response.status === 200) {
    const responseJSON = await response.json();
    try {
      const clients = z
        .object({ id: z.number(), issuer: z.string() })
        .array()
        .parse(parseApiResponse(responseJSON));

      return clients;
    } catch (error) {
      console.error('fetch issuers error:', error);
    }
  }
  return [];
};

export interface FetchClientsParams {
  search?: string;
  limit?: number;
  next_page?: string | null;
  deleted?: boolean;
}

const fetchClients = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  params?: FetchClientsParams,
): Promise<{ data: Client[]; next_page: string | null }> => {
  const token = await getToken();
  if (!token) return { data: [], next_page: null };
  const query = new URLSearchParams({ org: organizationUUID });
  if (params?.search) query.append('search', params.search);
  if (params?.limit !== undefined) query.append('limit', String(params.limit));
  if (params?.next_page) query.append('next_page', params.next_page);
  if (params?.deleted !== undefined) query.append('deleted', params.deleted ? 'true' : 'false');

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/clients?${query.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (response.status === 200) {
    const responseJSON = await response.json();
    try {
      const paginated = parsePaginatedResponse(responseJSON);
      if (paginated.data) {
        const clients = selectClientsSchema.parse(paginated.data);
        return { data: clients, next_page: paginated.next_page };
      } else {
        const clients = selectClientsSchema.parse(parseApiResponse(responseJSON));
        return { data: clients, next_page: null };
      }
    } catch (error) {
      console.error('fetch clients error:', error);
    }
  }
  return { data: [], next_page: null };
};

const addClient = async (
  getToken: () => Promise<string | null>,
  name: string,
  organizationUUID: string,
  reference?: string,
) => {
  let data: { name: string; reference?: string } = {
    name,
  };
  if (reference) {
    data = {
      ...data,
      reference,
    };
  }
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/clients?org=${organizationUUID}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    },
  );

  return parseApiResult(response);
};

const updateClient = async (
  getToken: () => Promise<string | null>,
  name: string,
  organizationUUID: string,
  clientUUID: string,
  reference?: string,
) => {
  let data: { name: string; clientUUID: string; reference?: string } = {
    name,
    clientUUID,
  };
  if (reference) {
    data = {
      ...data,
      reference,
    };
  }
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/clients?org=${organizationUUID}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    },
  );

  return parseApiResult(response);
};

const deleteClient = async (token: string, clientUUID: string, organizationUUID: string) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/clients/${clientUUID}?org=${organizationUUID}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return parseApiResult(response);
};

const restoreClient = async (token: string, clientUUID: string, organizationUUID: string) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/clients/restore/${clientUUID}?org=${organizationUUID}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return parseApiResult(response);
};

const addCostCentre = async (
  getToken: () => Promise<string | null>,
  name: string,
  organizationUUID: string,
  reference?: string,
) => {
  let data: { name: string; reference?: string } = {
    name,
  };
  if (reference) {
    data = {
      ...data,
      reference,
    };
  }
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/costcentres?org=${organizationUUID}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    },
  );

  return parseApiResult(response);
};

const updateReportStatus = async (
  getToken: () => Promise<string | null>,
  uuid: string,
  status: ReportStatus,
  comment?: string,
) => {
  const token = await getToken();
  const response = await fetch(`${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/reports/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ uuid, status, comment }),
  });

  return parseApiResult(response);
};

const addReport = async (
  getToken: () => Promise<string | null>,
  title: string,
  description: string,
  organizationUUID: string,
) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/reports?org=${organizationUUID}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ title, description, organizationUUID }),
    },
  );

  return parseApiResult(response);
};

const deleteReport = async (
  getToken: () => Promise<string | null>,
  reportUUID: string,
  organizationUUID: string,
): Promise<ApiResult> => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/reports/${reportUUID}?org=${organizationUUID}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return parseApiResult(response);
};

const editReport = async (
  getToken: () => Promise<string | null>,
  reportUUID: string,
  title: string,
  description: string,
  organizationUUID: string,
): Promise<ApiResult> => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/reports?org=${organizationUUID}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ uuid: reportUUID, title, description }),
    },
  );
  return parseApiResult(response);
};

const addMileage = async (
  getToken: () => Promise<string | null>,
  mileage: Omit<MilageInsert, 'organization_id'>,
  organizationUUID: string,
  comment?: string,
) => {
  const token = await getToken();
  const response = await fetch(`${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/mileage/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: comment
      ? JSON.stringify({
          mileage,
          organizationUUID,
          comment,
        })
      : JSON.stringify({ mileage, organizationUUID }),
  });

  return parseApiResult(response);
};

const fetchRules = async (getToken: () => Promise<string | null>, organizationUUID: string) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/rules?org=${organizationUUID}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (response.status === 200) {
    const responseJSON = await response.json();
    try {
      const rules = selectRulesSchema.parse(parseApiResponse(responseJSON));

      return rules;
    } catch (error) {
      console.error('fetch rules error:', error);
    }
  }
  return [];
};

const addRule = async (
  getToken: () => Promise<string | null>,
  categoryId: number | null,
  key: Rules,
  value: number,
  organizationUUID: string,
) => {
  const token = await getToken();
  const response = await fetch(`${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/rules/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ categoryId, key, value, organizationUUID }),
  });

  return parseApiResult(response);
};

const updateRule = async (
  getToken: () => Promise<string | null>,
  id: number,
  key: Rules,
  value: number,
) => {
  const token = await getToken();
  const response = await fetch(`${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/rules/`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ ruleId: id, key, value }),
  });

  return parseApiResult(response);
};

const deleteRule = async (getToken: () => Promise<string | null>, id: number, key: Rules) => {
  const token = await getToken();
  const response = await fetch(`${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/rules/`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ id, key }),
  });

  return parseApiResult(response);
};

const updateMileage = async (
  getToken: () => Promise<string | null>,
  mileage: MileageUpdate,
  comment?: string,
) => {
  const token = await getToken();
  const response = await fetch(`${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/mileage/`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: comment
      ? JSON.stringify({
          mileage,
          comment,
        })
      : JSON.stringify({ mileage }),
  });

  return parseApiResult(response);
};

export type FetchMileageResult = FetchResult<MileageWithHistory>;

const fetchMileage = async (
  getToken: () => Promise<string | null>,
  id: string,
): Promise<FetchMileageResult> =>
  fetchTypedResource({
    getToken,
    url: `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/mileage/${id}`,
    schema: selectMileageWithHistorySchema,
    component: 'fetchMileage',
    resourceId: id,
  });

const fetchCategories = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string | undefined,
  lng: string = 'en',
) => {
  if (!organizationUUID) return [];
  try {
    const token = await getToken();
    const result = await fetch(
      `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/categories?org=${organizationUUID}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'Accept-Language': lng,
        },
      },
    );
    const resultParsed = await result.json();

    const categoriesParsed = selectCategorySchema
      .extend({
        // rules: selectRulesSchema.array(),
        rules: z.array(selectRuleSchema),
      })
      .array()
      .parse(parseApiResponse(resultParsed));
    const categories: CategoryWithRules[] = [];
    categoriesParsed.forEach((category) => {
      const rules = new Map<Rules, Rule>();
      category.rules.forEach((rule) => {
        rules.set(rule.key, rule);
      });
      // filter rules from category as we transferred it from array to map
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      // const { rules: rulesArray, ...categoryWithoutRules } = category;

      categories.push({ ...category, rules });
    });

    // const categories = categoriesParsed.map((category) => ({
    //   ...category,
    //   rules: new Map(category.rules.map((rule) => [rule.key, rule])),
    // }));

    return categories;
  } catch (error) {
    console.error('error fetchCategories', error);
    return [];
  }
};

const addCategory = async (
  getToken: () => Promise<string | null>,
  title: string,
  description: string,
  costtype: number,
  organizationUUID: string,
) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/categories?org=${organizationUUID}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        title,
        description,
        costtype,
      }),
    },
  );

  return parseApiResult(response);
};

const updateCategory = async (
  getToken: () => Promise<string | null>,
  id: number,
  isEnabled: boolean,
  costtype: number,
  organizationUUID: string,
) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/categories/${id}?org=${organizationUUID}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ isEnabled, costtype, organizationUUID }),
    },
  );
  return parseApiResult(response);
};

// const upload = async (file: File, cloudId: string, name: string, uploader: any) => {
const uploadFile = async (
  token: string,
  key: string,
  content: File | Blob | string,
  cloudId: string,
  signal?: AbortSignal,
  flowId?: string | null,
): Promise<{ cloudId: string; error: boolean | null; flowId: string | null }> => {
  try {
    const type = typeof content === 'string' ? 'application/json' : (content as File | Blob).type;
    const size =
      typeof content === 'string' ? content.length : ((content as File | Blob).size ?? 0);

    // Extract the resource type from the key pattern (e.g., "expenses/orgId/..." or "settlements/orgId/...")
    const resourceType = key.split('/')[0];
    const name = resourceType === 'expenses' ? 'receipt.pdf' : 'settlement.pdf';
    const objectKey = `${key}/${cloudId}/${name}`;

    const presignResponse = await fetch(
      `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/s3/presigned-post`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ key: objectKey, type, size }),
        signal,
      },
      flowId,
    );
    const presignJson = await presignResponse.json();

    // Support response parsing via parseApiResponse
    const presignData = parseApiResponse(presignJson) || presignJson;
    const { fields, url } = presignData || {};

    if (!fields || !url) {
      console.error('Invalid presign response shape', presignJson);
      return { cloudId, error: true, flowId: flowId ?? null };
    }

    const formDataPost = new FormData();
    Object.entries(fields).forEach(([fieldName, value]) => {
      const val: string | Blob = typeof value === 'string' ? value : (value as Blob);
      formDataPost.append(fieldName, val);
    });
    formDataPost.append('file', content as Blob);

    const result = await fetch(url, {
      method: 'POST',
      body: formDataPost,
      signal,
    });

    if (result.status === 204) {
      return { cloudId, error: null, flowId: flowId ?? null };
    }
    return { cloudId, error: true, flowId: flowId ?? null };
  } catch (error) {
    errorTracker.captureException(error as Error, {
      flowId: flowId || undefined,
      tags: { message: 'Error uploading file' },
    });
    return { cloudId, error: true, flowId: flowId ?? null };
  }
};

const deleteExpense = async (
  token: string,
  cloudId: string,
  organizationUUID: string,
): Promise<ApiResult> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/expenses/delete/${cloudId}?org=${organizationUUID}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return parseApiResult(response);
};

const deleteMileage = async (
  token: string,
  cloudId: string,
  organizationUUID: string,
): Promise<ApiResult> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/mileage/delete/${cloudId}?org=${organizationUUID}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return parseApiResult(response);
};

const deleteReceipt = async (token: string, cloudId: string, organizationUUID: string) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/expenses/delete-receipt/${cloudId}?org=${organizationUUID}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return parseApiResult(response);
};

const ocrSplitSchema = z.object({
  amount: z.string(),
  currency: z.string(),
  category: z.string().nullable(),
  taxRate: z.string(),
});

export type OcrSplit = z.infer<typeof ocrSplitSchema>;

/** Matches backend OcrResultData (BedrockExtractResult + optional similarVendors). */
const ocrDataSchema = z.object({
  vendorName: z.string().nullable(),
  invoiceDate: z.string().nullable(),
  total: z.string().nullable(),
  tax: z.string().nullable(),
  taxRate: z.string().nullable(),
  currency: z.string().nullable(),
  /** ISO 3166-1 alpha-2, e.g. "CH", "DE". */
  country: z.string().nullable(),
  category: z.string().nullable(),
  /** Null/omitted when there is a single VAT rate or no breakdown. */
  splits: z.array(ocrSplitSchema).nullable().optional(),
  latencyMs: z.number(),
  error: z.string().nullable(),
  similarVendors: z.object({ name: z.string(), expenseCount: z.number() }).array().optional(),
});

export type OcrData = z.infer<typeof ocrDataSchema>;

const ocrResultSchema = z.object({
  status: z.enum(['IN_PROGRESS', 'FAILED', 'PARTIAL_SUCCESS', 'SUCCEEDED']),
  data: ocrDataSchema.optional(),
});

const initOcr = async (
  getToken: () => Promise<string | null>,
  cloudId: string,
  orgId: string,
  flowId?: string | null,
) => {
  try {
    const token = await getToken();
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/ocr/init`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          cloudId,
          orgId,
        }),
      },
      flowId,
    );
    const result = await response.json();
    const payload = parseApiResponse(result);

    // Backend decides whether OCR is processed synchronously or asynchronously.
    // - Sync:  { status: 'SUCCEEDED' | 'FAILED' | 'PARTIAL_SUCCESS', data: {...} }
    // - Async: { status: 'IN_PROGRESS', jobId: '...' }
    const ocrInitSchema = z.discriminatedUnion('status', [
      // Async: job in progress, jobId required, no data expected
      z.object({
        status: z.literal('IN_PROGRESS'),
        jobId: z.string(),
        data: z.undefined().optional(),
      }),
      // Failed: may or may not contain data or jobId
      z.object({
        status: z.literal('FAILED'),
        jobId: z.string().optional(),
        data: ocrDataSchema.optional(),
      }),
      // Partial success: data is required
      z.object({
        status: z.literal('PARTIAL_SUCCESS'),
        jobId: z.string().optional(),
        data: ocrDataSchema,
      }),
      // Success: data is required
      z.object({
        status: z.literal('SUCCEEDED'),
        jobId: z.string().optional(),
        data: ocrDataSchema,
      }),
    ]);

    const parsed = ocrInitSchema.safeParse(payload);
    if (parsed.error) {
      console.error('error initOcr', parsed.error);
      return null;
    }

    return parsed.data;
  } catch (error) {
    console.error('error initOcr', error);
    return null;
  }
};

const initOcrSync = async (
  getToken: () => Promise<string | null>,
  cloudId: string,
  orgId: string,
  flowId?: string | null,
) => {
  try {
    const token = await getToken();
    if (!token || typeof token !== 'string' || token.trim() === '') {
      console.error('error initOcrSync: No valid token available');
      return null;
    }
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/ocr/init-sync`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          cloudId,
          orgId,
        }),
      },
      flowId,
    );
    if (response.status === 200) {
      const result = await response.json();
      const payload = parseApiResponse(result);
      const ocrResult = ocrResultSchema.safeParse(payload);
      if (ocrResult.error) {
        console.error('error initOcrSync', ocrResult.error);
        return null;
      }
      return ocrResult.data;
    }
    return null;
  } catch (error) {
    console.error('error initOcrSync', error);
    return null;
  }
};

const getOcrDetails = async (
  getToken: () => Promise<string | null>,
  jobId: string,
  organizationUUID: string,
  cloudId: string,
  flowId?: string,
) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/ocr/details/${jobId}?org=${organizationUUID}&cloudId=${encodeURIComponent(cloudId)}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
    flowId,
  );
  if (response.status === 200) {
    const result = await response.json();
    const payload = parseApiResponse(result);
    const ocrResult = ocrResultSchema.safeParse(payload);
    if (ocrResult.error) {
      console.error('error getOcrDetails', ocrResult.error);
      return null;
    }
    return ocrResult.data;
  }
  return null;
};

const downloadPdf = async (
  getToken: () => Promise<string | null>,
  cloudId: string,
  orgId: string,
) => {
  //  const response = await fetch('/api/v1/s3/generate-temporary-url', {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/s3/generate-temporary-url`,
    {
      method: 'POST',
      body: JSON.stringify({
        key: `expenses/${orgId}`,
        cloudId: cloudId,
        name: 'receipt.pdf',
        isReceipt: true,
      }),
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
  if (response.status === 201) {
    const result = await response.json();
    const payload = parseApiResponse<{ temporaryUrl: string }>(result);
    return payload?.temporaryUrl;
  } else {
    const json = await response.json();
    console.error('error downloadPdf', json);
    return null;
  }
};

const bulkStatusChangeReports = async (
  getToken: () => Promise<string | null>,
  items: string[],
  status: ExpenseStatus,
): Promise<ApiResult> => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/reports/status/bulk`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ items, status }),
    },
  );
  return parseApiResult(response);
};

const bulkStatusChange = async (
  getToken: () => Promise<string | null>,
  expenses: string[],
  mileages: string[],
  status: ExpenseStatus,
  comment?: string,
): Promise<ApiResult> => {
  const token = await getToken();
  const response = await fetch(`${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/todos/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ expenses, mileages, status, comment }),
  });
  return parseApiResult(response);
};

const editOrganization = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  costCentreId?: number | null,
  defaultCurrency?: string | null,
  overdueDays?: number | null,
  recipients?: string[] | null,
  defaultCountry?: string | null,
  perKmExpenseRate?: string | null,
  defaultPaidWithPersonalFunds?: boolean | null,
  formFieldSettings?: any | null,
  accountantRoleEnabled?: boolean | null,
) => {
  const token = await getToken();
  const data: {
    default_cost_centre_id?: number | null;
    default_currency?: string | null;
    overdue_days?: number | null;
    recipients?: string[] | null;
    default_country?: string | null;
    per_km_expense_rate?: string | null;
    default_paid_with_personal_funds?: boolean | null;
    form_field_settings?: any | null;
    accountant_role_enabled?: boolean | null;
  } = {};
  if (defaultCurrency !== undefined) {
    data.default_currency = defaultCurrency;
  }
  if (costCentreId !== undefined) {
    data.default_cost_centre_id = costCentreId;
  }
  if (overdueDays !== undefined) {
    data.overdue_days = overdueDays;
  }
  if (recipients !== undefined) {
    data.recipients = recipients;
  }
  if (defaultCountry !== undefined) {
    data.default_country = defaultCountry;
  }
  if (perKmExpenseRate !== undefined) {
    data.per_km_expense_rate = perKmExpenseRate;
  }
  if (defaultPaidWithPersonalFunds !== undefined) {
    data.default_paid_with_personal_funds = defaultPaidWithPersonalFunds;
  }
  if (formFieldSettings !== undefined) {
    data.form_field_settings = formFieldSettings;
  }
  if (accountantRoleEnabled !== undefined) {
    data.accountant_role_enabled = accountantRoleEnabled;
  }

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization?org=${organizationUUID}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    },
  );

  return parseApiResult(response);
};

const updateCompanySetup = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  payload: {
    company_setup_status?: CompanySetupStatus;
    company_setup_steps?: CompanySetupSteps;
  },
) => {
  const token = await getToken();
  const data: {
    company_setup_status?: CompanySetupStatus;
    company_setup_steps?: CompanySetupSteps;
  } = {};
  if (payload.company_setup_status !== undefined) {
    data.company_setup_status = payload.company_setup_status;
  }
  if (payload.company_setup_steps !== undefined) {
    data.company_setup_steps = payload.company_setup_steps;
  }

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization?org=${organizationUUID}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    },
  );

  if (response.status === 200) {
    return true;
  }
  const json = await response.json().catch(() => null);
  console.error(json);
  throw new Error('Failed to update company setup');
};

const updateSetupBanner = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  hideCompanySetupBanner: boolean,
) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/setup-banner?org=${organizationUUID}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ hide_company_setup_banner: hideCompanySetupBanner }),
    },
  );

  if (response.status === 200) {
    return true;
  }
  const json = await response.json().catch(() => null);
  console.error(json);
  throw new Error('Failed to update setup banner');
};

const updateCostCentre = async (
  getToken: () => Promise<string | null>,
  costCentreId: number | null,
  name: string,
  reference: string,
  organizationUUID: string,
  isDeleted: boolean,
) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/costcentres?org=${organizationUUID}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        costCentreId,
        name,
        reference,
        organizationUUID,
        is_deleted: isDeleted,
      }),
    },
  );

  return parseApiResult(response);
};

const fetchExchangeRates = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string | undefined,
) => {
  if (!organizationUUID) return [];
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/exchange-rates?org=${organizationUUID}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (response.status === 200) {
    const result = await response.json();
    const exchangeRatesSchema = z.array(
      z.object({
        currency: z.string(),
        rate: z.number(),
      }),
    );
    const [error, exchangeRates] = tryCatch(() =>
      exchangeRatesSchema.parse(parseApiResponse(result)),
    );
    if (error) {
      errorTracker.captureException(error, { tags: { message: 'Error parsing exchange rates' } });
      return [];
    }
    return exchangeRates;
  }
  return [];
};

const fetchSettlement = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string | undefined,
  id: string,
) => {
  const token = await getToken();
  if (!token || !organizationUUID) return null;
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/settlements/${id}?org=${organizationUUID}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (response.status === 200) {
    const json = await response.json();
    return parseApiResponse<Settlement>(json);
  }
  return null;
};

const editSettlement = async (token: string, settlement: Settlement, organizationUUID: string) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/settlements?org=${organizationUUID}`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        uuid: settlement.uuid,
        title: settlement.title,
        from: settlement.from,
        to: settlement.to,
        currency: settlement.currency,
        status: settlement.status,
      }),
    },
  );
  return parseApiResult(response);
};

const deleteSettlement = async (
  token: string,
  settlement: Settlement,
  organizationUUID: string,
) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/settlements/${settlement.uuid}?org=${organizationUUID}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return parseApiResult(response);
};

const createSettlement = async (
  token: string,
  uuid: string,
  organizationUUID: string,
  title: string,
  from: string,
  to: string,
  defaultCurrency: string,
) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/settlements?org=${organizationUUID}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uuid, title, from, to, defaultCurrency }),
    },
  );
  return parseApiResult(response);
};

const fetchSettlements = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string | undefined,
) => {
  if (!organizationUUID) return [];
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/settlements?org=${organizationUUID}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (response.status === 200) {
    const respJSON = await response.json();
    return parseApiResponse<Settlement[]>(respJSON) || [];
  }
  return [];
};

const unlinkSettlementTransaction = async (
  token: string,
  transactionId: number,
  organizationUUID: string,
) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/settlements/transactions/unlink/?org=${organizationUUID}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ transactionId }),
    },
  );
  return parseApiResult(response);
};

const linkSettlementTransaction = async (
  token: string,
  transactionId: number,
  expenseId: number,
  organizationUUID: string,
) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/settlements/transactions/link/?org=${organizationUUID}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ transactionId, expenseId }),
    },
  );
  return parseApiResult(response);
};

const editSettlementTransaction = async (
  token: string,
  transaction: SettlementTransaction,
  organizationUUID: string,
) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/settlements/transactions/edit/?org=${organizationUUID}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(transaction),
    },
  );
  return parseApiResult(response);
};

const addExpenseComment = async (
  token: string,
  organizationUUID: string,
  expenseUUID: string,
  comment: string,
): Promise<ApiResult> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/expenses/comment?org=${organizationUUID}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ expenseUUID, comment }),
    },
  );
  return parseApiResult(response);
};

const addMileageComment = async (
  token: string,
  organizationUUID: string,
  mileageUUID: string,
  comment: string,
) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/mileage/comment?org=${organizationUUID}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mileageUUID, comment }),
    },
  );
  return parseApiResult(response);
};

const getYTDComparisonData = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string | undefined,
  role: Roles,
): Promise<Array<{ month: number; [key: string]: number }>> => {
  if (!organizationUUID) return [];
  const token = await getToken();
  try {
    const response = await fetch(
      `${
        process.env.NEXT_PUBLIC_DOCOMONDO_URL
      }/api/v1/expenses/ytd-comparison?org=${organizationUUID}${role === 'manager' || role === 'accountant' ? '&m=true' : ''}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (response.status === 200) {
      type ApiDataItem = { [key: string]: string };
      interface YTDDataItem {
        month: number;
        [key: string]: number;
      }

      const responseJSON = await response.json();
      const rawData = (parseApiResponse(responseJSON) ?? []) as ApiDataItem[];

      if (!Array.isArray(rawData)) {
        return [];
      }

      const convertedData: YTDDataItem[] = rawData.map((item: ApiDataItem) => {
        // Ensure month is always present
        const result: YTDDataItem = {
          month: parseFloat(item.month || '0'),
        };

        // Convert other properties to numbers
        Object.keys(item).forEach((key) => {
          if (key !== 'month' && item[key] !== undefined) {
            result[key] = parseFloat(item[key] || '0');
          }
        });

        return result;
      });

      return convertedData;
    }
  } catch (error) {
    console.error('Error fetching YTD comparison data:', error);
    return [];
  }
  return [];
};

const getCategoryChartData = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string | undefined,
  role: Roles,
  from: Date | undefined,
  to: Date | undefined,
  lng: string,
) => {
  if (!organizationUUID || !from || !to) return [];
  const token = await getToken();
  const dateFrom = format(from, 'yyyy-MM-dd');
  const dateTo = format(to, 'yyyy-MM-dd');
  const response = await fetch(
    `${
      process.env.NEXT_PUBLIC_DOCOMONDO_URL
    }/api/v1/expenses/category-chart?org=${organizationUUID}&from=${dateFrom}&to=${dateTo}&lng=${lng}${
      role === 'manager' || role === 'accountant' ? '&m=true' : ''
    }`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (response.status === 200) {
    const apiSchema = z.object({
      name: z.string(),
      value: z.number(),
    });
    const rawData = await response.json();
    const parsed = apiSchema.array().parse(parseApiResponse(rawData));
    return parsed;
  }
  return [];
};

const getAreaChartData = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string | undefined,
  role: Roles,
  from: Date,
  to: Date,
  userIds: string[],
  isOwedToEmployee: boolean,
  isPrivateExpense: boolean,
  amountFrom: number,
  amountTo: number,
  selectedClients: string[],
  selectedCostcentres: string[],
): Promise<Array<{ month: string; value: number }>> => {
  if (!organizationUUID) return [];
  const token = await getToken();

  const _amountFrom = amountFrom === 0 || isNaN(amountFrom) ? undefined : amountFrom;
  const _amountTo =
    amountTo === 0 || amountTo === Number.POSITIVE_INFINITY || isNaN(amountTo)
      ? undefined
      : amountTo;

  // Build params object
  const params: Record<string, string> = {
    org: organizationUUID,
    m: role === 'manager' || role === 'accountant' ? 'true' : 'false',
  };
  if (from) params.from = from.toISOString();
  if (to) params.to = to.toISOString();
  if (userIds && userIds.length > 0) params.userIds = userIds.join(',');
  if (isOwedToEmployee !== undefined) params.owed = String(isOwedToEmployee);
  if (isPrivateExpense !== undefined) params.private = String(isPrivateExpense);
  if (_amountFrom !== undefined) params.agte = String(_amountFrom);
  if (_amountTo !== undefined) params.alte = String(_amountTo);
  if (selectedClients && selectedClients.length > 0) params.clients = selectedClients.join(',');
  if (selectedCostcentres && selectedCostcentres.length > 0)
    params.costcentres = selectedCostcentres.join(',');

  const queryString = new URLSearchParams(params).toString();

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/expenses/area-chart?${queryString}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (response.status === 200) {
    const apiSchema = z
      .object({
        month: z.string(),
        value: z.number(),
      })
      .array();

    const responseJSON = await response.json();
    const rawData = parseApiResponse(responseJSON);
    const [error, parsed] = tryCatch(() => apiSchema.parse(rawData));
    if (error) {
      return [];
    }

    return parsed;
  }
  return [];
};

const updateUserReminderLocale = async (token: string, locale: 'en' | 'de') => {
  const response = await fetch(`${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/user/language`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ language: locale }),
  });
  if (response.status === 200) {
    return true;
  }
  const json = await response.json().catch(() => ({}));
  console.error('updateUserReminderLocale failed', json);
  return false;
};

const fetchBankDetails = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
): Promise<BankDetails | null> => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/bank-details?org=${organizationUUID}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (response.status === 200) {
    const responseJSON = await response.json();
    return parseApiResponse<BankDetails>(responseJSON) ?? null;
  }
  return null;
};

const updateUserBankDetails = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  iban?: string | null,
): Promise<ApiResult> => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/bank-details?org=${organizationUUID}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ iban: iban || null }),
    },
  );
  return parseApiResult(response);
};

const fetchDuplicates = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  role: Roles,
) => {
  const token = await getToken();
  const asAdmin = role === 'manager' || role === 'accountant';
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/expenses/duplicates?org=${organizationUUID}${asAdmin ? '&m=true' : ''}`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (response.status === 200) {
    const respJSON = await response.json();
    const [error, data] = tryCatch(() =>
      selectExpensesSchema.array().array().parse(parseApiResponse(respJSON)),
    );
    if (error) {
      errorTracker.captureException(error, { tags: { message: 'Error parsing duplicates' } });
      return [];
    }
    return data;
  }
  return [];
};

const ignoreDuplicate = async (token: string, uuid: string) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/expenses/duplicate/ignore/${uuid}`,
    {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return parseApiResult(response);
};

const getRoleForBackend = (role: string) => {
  const ROLESMAP = {
    Member: 'basic_member',
    Manager: 'org:manager',
    Accountant: 'org:accountant',
    Admin: 'admin',
  };
  const backendRole = ROLESMAP[role as keyof typeof ROLESMAP];
  if (!backendRole) {
    errorTracker.captureMessage('Invalid role', 'warning');
    return 'basic_member';
  }
  return backendRole;
};

async function updateUserAppInfo(apiToken: string, browser: string) {
  // track last active of user with current device
  const version_code = 'web';
  const platform = 'web';

  if (!apiToken) {
    return false;
  }

  const data = {
    app: 'EXPENSES',
    platform,
    device_id: browser,
    platform_version: version_code,
  };
  const result = await fetch(`${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/user/activity`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + apiToken,
    },
    body: JSON.stringify(data),
  });

  await result.json();
}

/**
 * Creates a Stripe checkout session for the given price, email, and orgId.
 */
const createCheckoutSession = async (
  token: string,
  email: string,
  orgId: string,
  quantity: number,
  period: 'monthly' | '6months',
  redirectUrl?: string,
): Promise<string | null> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/stripe/create-checkout-session`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        email,
        orgId,
        redirectUrl: redirectUrl || `${process.env.NEXT_PUBLIC_EXPENSES_URL ?? ''}/organizations`,
        quantity,
        period,
      }),
    },
  );
  if (!response.ok) {
    throw new Error('Failed to create checkout session');
  }
  const schema = z.object({ sessionId: z.string() });
  const parsed = await response.json();
  const [error, data] = tryCatch(() => schema.parse(parseApiResponse(parsed)));
  if (error) {
    return null;
  }
  return data.sessionId;
};

const createBillingPortalSession = async (token: string, orgId: string): Promise<string | null> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/stripe/create-billing-portal-session?org=${orgId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        returnUrl: `${process.env.NEXT_PUBLIC_EXPENSES_URL ?? ''}/organizations`,
        orgId,
      }),
    },
  );
  if (!response.ok) {
    throw new Error('Failed to create billing portal session');
  }
  const schema = z.object({ url: z.string() });
  const parsed = await response.json();
  const [error, data] = tryCatch(() => schema.parse(parseApiResponse(parsed)));
  if (error) {
    return null;
  }
  return data.url;
};

const fetchMerchantMappings = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  search?: string,
  limit?: number,
) => {
  const token = await getToken();
  const query = new URLSearchParams({ org: organizationUUID });
  if (search) query.append('search', search);
  if (limit !== undefined) query.append('limit', String(limit));

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/merchant-mappings?${query.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (response.status === 200) {
    const responseJSON = await response.json();
    try {
      const mappings = z
        .object({
          id: z.number(),
          organization_id: z.number(),
          original_merchant: z.string(),
          display_merchant: z.string(),
          created_at: z.string(),
          updated_at: z.string(),
        })
        .array()
        .parse(parseApiResponse(responseJSON));

      return mappings;
    } catch (e) {
      console.error('Error parsing merchant mappings', e);
      return [];
    }
  } else {
    return [];
  }
};

const createMerchantMapping = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  originalMerchant: string,
  displayMerchant: string,
) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/merchant-mappings?org=${organizationUUID}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        original_merchant: originalMerchant,
        display_merchant: displayMerchant,
      }),
    },
  );

  if (response.status === 201) {
    return true;
  } else {
    console.error('Error creating merchant mapping');
    return false;
  }
};

const updateMerchantMapping = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  originalMerchant: string,
  displayMerchant: string,
) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/merchant-mappings?org=${organizationUUID}&original_merchant=${encodeURIComponent(originalMerchant)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        display_merchant: displayMerchant,
      }),
    },
  );

  if (response.status === 200) {
    return true;
  } else {
    console.error('Error updating merchant mapping');
    return false;
  }
};

const deleteMerchantMapping = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  originalMerchant: string,
) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/merchant-mappings?org=${organizationUUID}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        original_merchant: originalMerchant,
      }),
    },
  );

  if (response.status === 200) {
    return true;
  } else {
    console.error('Error deleting merchant mapping');
    return false;
  }
};

const rejectMerchantSuggestion = async (
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  originalMerchant: string,
  rejectedMerchant: string,
) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/organization/merchant-suggestion-rejections?org=${organizationUUID}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        original_merchant: originalMerchant, // what ocr detected
        rejected_merchant: rejectedMerchant, // existing
      }),
    },
  );

  if (response.status === 201 || response.status === 200) {
    return true;
  } else {
    console.error('Error rejecting merchant suggestion');
    return false;
  }
};

export default {
  fetchTodos,
  fetchFilteredExpenses,
  fetchReports,
  fetchReportOptions,
  fetchEmployees,
  fetchOrganization,
  trackConversion,
  createOrganization,
  fetchOrganizationMemberships,
  fetchUserMemberships,
  fetchSeatStatus,
  fetchOrganizationImage,
  uploadOrganizationImage,
  deleteOrganizationImage,
  getUpcomingReceipt,
  updateSeats,
  updateMembershipRole,
  inviteUsers,
  removeMember,
  revokeInvite,
  fetchReport,
  shareReport,
  shareExpenses,
  fetchExpense,
  fetchExpenses,
  fetchSettlement,
  editSettlement,
  deleteSettlement,
  addExpense,
  fetchCostCentres,
  addCostCentre,
  fetchUser,
  fetchClients,
  fetchIssuers,
  addClient,
  updateClient,
  deleteClient,
  restoreClient,
  updateExpense,
  updateReportStatus,
  addReport,
  deleteReport,
  editReport,
  addMileage,
  updateMileage,
  fetchMileage,
  fetchCategories,
  addCategory,
  updateCategory,
  addRule,
  fetchRules,
  updateRule,
  deleteRule,
  uploadFile,
  deleteReceipt,
  deleteExpense,
  deleteMileage,
  downloadPdf,
  bulkStatusChange,
  bulkStatusChangeReports,
  editOrganization,
  updateCompanySetup,
  updateSetupBanner,
  updateCostCentre,
  initOcr,
  initOcrSync,
  getOcrDetails,
  fetchExchangeRates,
  createSettlement,
  fetchSettlements,
  unlinkSettlementTransaction,
  linkSettlementTransaction,
  editSettlementTransaction,
  addExpenseComment,
  addMileageComment,
  getYTDComparisonData,
  getCategoryChartData,
  getAreaChartData,
  fetchDuplicates,
  ignoreDuplicate,
  updateUserAppInfo,
  createCheckoutSession,
  createBillingPortalSession,
  updateUserReminderLocale,
  fetchBankDetails,
  updateUserBankDetails,
  fetchMerchantMappings,
  createMerchantMapping,
  updateMerchantMapping,
  deleteMerchantMapping,
  rejectMerchantSuggestion,
  fetchTodosSummary,
  fetchReportsSummary,
};

// ---------------------------------------------------------------------------
// Summary endpoints (DOC-339)
// ---------------------------------------------------------------------------

async function fetchTodosSummary(
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  role: Roles,
): Promise<TodosSummary | null> {
  const token = await getToken();
  if (!token) return null;

  const isManager = role === 'manager' || role === 'accountant';
  const params = new URLSearchParams({
    org: organizationUUID,
    m: isManager ? 'true' : 'false',
  });

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/todos/summary?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (response.status !== 200) return null;
  try {
    const json = await response.json();
    const raw = parseApiResponse(json);
    return TodosSummarySchema.parse(raw);
  } catch (error) {
    errorTracker.captureException(error as Error, {
      tags: { message: 'Error parsing todos summary' },
    });
    return null;
  }
}

async function fetchReportsSummary(
  getToken: () => Promise<string | null>,
  organizationUUID: string,
  role: Roles,
): Promise<ReportsSummary | null> {
  const token = await getToken();
  if (!token) return null;

  const params = new URLSearchParams({
    org: organizationUUID,
    role,
  });

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_DOCOMONDO_URL}/api/v1/reports/summary?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (response.status !== 200) return null;
  try {
    const json = await response.json();
    const raw = parseApiResponse(json);
    return ReportsSummarySchema.parse(raw);
  } catch (error) {
    errorTracker.captureException(error as Error, {
      tags: { message: 'Error parsing reports summary' },
    });
    return null;
  }
}
